import * as React from "react";

export type CollabToastType = "error" | "success" | "info" | "warning";

type ToastItem = {
  id: number;
  type: CollabToastType;
  text: string;
};

type Listener = (items: ToastItem[]) => void;

let nextId = 1;
let items: ToastItem[] = [];
const listeners = new Set<Listener>();
const timers = new Map<number, number>();
const DEFAULT_MS = 3200;

function emit() {
  listeners.forEach((fn) => fn([...items]));
}

function dismiss(id: number) {
  const t = timers.get(id);
  if (t != null) {
    window.clearTimeout(t);
    timers.delete(id);
  }
  items = items.filter((i) => i.id !== id);
  emit();
}

function push(type: CollabToastType, text: string, durationMs = DEFAULT_MS) {
  const id = nextId++;
  items = [...items.slice(-2), { id, type, text }];
  emit();
  if (durationMs > 0) {
    timers.set(
      id,
      window.setTimeout(() => dismiss(id), durationMs),
    );
  }
  return id;
}

/** Drop-in replacement for antd `message.*` inside collaborate. */
export const showCollabToast = {
  error: (text: string) => push("error", text),
  success: (text: string) => push("success", text),
  info: (text: string) => push("info", text),
  warning: (text: string) => push("warning", text),
};

export const CollabToastHost: React.FC = () => {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);

  React.useEffect(() => {
    listeners.add(setToasts);
    setToasts([...items]);
    return () => {
      listeners.delete(setToasts);
    };
  }, []);

  if (toasts.length === 0) {
    return null;
  }

  return (
    <div className="collab-toast-host" aria-live="polite">
      {toasts.map((t) => (
        <button
          key={t.id}
          type="button"
          className={`collab-toast collab-toast--${t.type}`}
          onClick={() => dismiss(t.id)}
        >
          {t.text}
        </button>
      ))}
    </div>
  );
};
